import React from 'react';
import {
    Box,
    Button,
    Chip,
    Divider,
    FormControl,
    FormControlLabel,
    Grid,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Stack,
    Switch,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import TroubleshootOutlinedIcon from '@mui/icons-material/TroubleshootOutlined';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import DeleteSweepRoundedIcon from '@mui/icons-material/DeleteSweepRounded';
import PlayCircleFilledWhiteRoundedIcon from '@mui/icons-material/PlayCircleFilledWhiteRounded';
import { subscribeToLogs, logDebug, logError, logInfo, logWarn } from '../../logging/loggingService';
import { API_BASE_URL } from '../../utils/apiBaseUrl';

const DEFAULT_MAX_ENTRIES = 400;
const DEFAULT_LEVEL = (process.env.REACT_APP_DEVOPS_DEFAULT_LEVEL || 'all').toLowerCase();
const LEVEL_OPTIONS = [
    { value: 'all', label: 'Alle' },
    { value: 'error', label: 'Error' },
    { value: 'warn', label: 'Warn' },
    { value: 'info', label: 'Info' },
    { value: 'debug', label: 'Debug' },
    { value: 'trace', label: 'Trace' },
];

const LEVEL_COLOR = {
    error: 'error',
    warn: 'warning',
    info: 'primary',
    debug: 'default',
    trace: 'default',
};

const LOGGING_ENDPOINT = process.env.REACT_APP_LOGGING_ENDPOINT || '/api/logs/frontend';
const REMOTE_LOGGING_URL = LOGGING_ENDPOINT.startsWith('http')
    ? LOGGING_ENDPOINT
    : `${API_BASE_URL}${LOGGING_ENDPOINT.startsWith('/') ? LOGGING_ENDPOINT : `/${LOGGING_ENDPOINT}`}`;
const REMOTE_LOGGING_ENABLED = process.env.REACT_APP_ENABLE_REMOTE_LOGGING !== 'false';

export default function DevOps() {
    const [entries, setEntries] = React.useState([]);
    const [levelFilter, setLevelFilter] = React.useState(() => {
        const allowed = LEVEL_OPTIONS.map((opt) => opt.value);
        return allowed.includes(DEFAULT_LEVEL) ? DEFAULT_LEVEL : 'all';
    });
    const [search, setSearch] = React.useState('');
    const [showStack, setShowStack] = React.useState(true);
    const [streamPaused, setStreamPaused] = React.useState(false);
    const [maxEntries, setMaxEntries] = React.useState(DEFAULT_MAX_ENTRIES);
    const mountedRef = React.useRef(false);

    React.useEffect(() => {
        const unsubscribe = subscribeToLogs((entry) => {
            if (streamPaused) {
                return;
            }
            setEntries((prev) => {
                const next = [entry, ...prev];
                return next.slice(0, maxEntries);
            });
        });
        if (!mountedRef.current) {
            mountedRef.current = true;
            logInfo('DevOps-Konsole geöffnet', { component: 'DevOps' });
        }
        return () => unsubscribe();
    }, [streamPaused, maxEntries]);

    const filteredEntries = React.useMemo(() => {
        return entries.filter((entry) => {
            if (levelFilter !== 'all' && entry.level !== levelFilter) {
                return false;
            }
            if (!search.trim()) {
                return true;
            }
            const haystack = [
                entry.message,
                entry.component,
                entry.stackTrace,
                entry.timestamp,
                JSON.stringify(entry.context || {}),
            ]
                .join(' ')
                .toLowerCase();
            return haystack.includes(search.trim().toLowerCase());
        });
    }, [entries, levelFilter, search]);

    const levelStats = React.useMemo(() => {
        return entries.reduce(
            (acc, entry) => {
                acc[entry.level] = (acc[entry.level] || 0) + 1;
                return acc;
            },
            { total: entries.length }
        );
    }, [entries]);

    const triggerProbe = (type) => {
        switch (type) {
            case 'warn':
                logWarn('Probe: Warnung ausgelöst', { component: 'DevOps', probe: true });
                break;
            case 'error':
                logError('Probe: Fehler simuliert', new Error('Simulierter Fehler'), { component: 'DevOps', probe: true });
                break;
            case 'debug':
                logDebug('Probe: Debug-Pfad', { component: 'DevOps', probe: true });
                break;
            default:
                logInfo('Probe: Info ausgelöst', { component: 'DevOps', probe: true });
                break;
        }
    };

    const clearEntries = () => setEntries([]);

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                pb: 6,
                background: (theme) => `linear-gradient(135deg, ${theme.palette.grey[100]}, ${theme.palette.background.default})`,
                minHeight: '100vh',
                borderRadius: 2,
                px: { xs: 1, md: 0 },
            }}
        >
            <Paper elevation={3} sx={{ p: 3, borderRadius: 3 }}>
                <Stack direction="row" alignItems="center" spacing={2} flexWrap="wrap" rowGap={1}>
                    <TroubleshootOutlinedIcon color="primary" fontSize="large" />
                    <Box>
                        <Typography variant="h5" fontWeight={700}>
                            DevOps Console
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Live-Logging, Telemetrie und Schnelltests für das Frontend.
                        </Typography>
                    </Box>
                    <Box sx={{ flexGrow: 1 }} />
                    <Stack direction="row" spacing={1}>
                        <Button
                            variant="outlined"
                            startIcon={<RefreshRoundedIcon />}
                            onClick={() => setEntries([...entries])}
                        >
                            Aktualisieren
                        </Button>
                        <Button
                            variant="outlined"
                            color="error"
                            startIcon={<DeleteSweepRoundedIcon />}
                            onClick={clearEntries}
                        >
                            Logs leeren
                        </Button>
                    </Stack>
                </Stack>
            </Paper>

            <Grid container spacing={2}>
                <Grid item xs={12} md={8}>
                    <Paper elevation={1} sx={{ p: 2.5, borderRadius: 3, minHeight: 420 }}>
                        <Stack direction="row" spacing={2} flexWrap="wrap" rowGap={1} mb={2}>
                            <FormControl size="small" sx={{ minWidth: 140 }}>
                                <InputLabel id="level-filter-label">Level</InputLabel>
                                <Select
                                    labelId="level-filter-label"
                                    value={levelFilter}
                                    label="Level"
                                    onChange={(event) => setLevelFilter(event.target.value)}
                                >
                                    {LEVEL_OPTIONS.map((option) => (
                                        <MenuItem key={option.value} value={option.value}>
                                            {option.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <TextField
                                size="small"
                                label="Suche"
                                placeholder="Suche in Message, Komponente oder Kontext"
                                value={search}
                                onChange={(event) => setSearch(event.target.value)}
                                sx={{ flexGrow: 1, minWidth: 220 }}
                            />
                            <FormControlLabel
                                control={<Switch checked={showStack} onChange={() => setShowStack((prev) => !prev)} />}
                                label="Stacktrace anzeigen"
                            />
                            <FormControlLabel
                                control={<Switch checked={streamPaused} onChange={() => setStreamPaused((prev) => !prev)} />}
                                label={streamPaused ? "Stream pausiert" : "Stream aktiv"}
                            />
                            <TextField
                                size="small"
                                type="number"
                                label="Max. Einträge"
                                value={maxEntries}
                                inputProps={{ min: 50, max: 1000, step: 50 }}
                                onChange={(event) => {
                                    const value = Number(event.target.value);
                                    if (Number.isFinite(value) && value >= 50) {
                                        setMaxEntries(Math.min(1000, value));
                                    }
                                }}
                                sx={{ width: 140 }}
                            />
                        </Stack>

                        <Stack spacing={1.5} sx={{ maxHeight: 520, overflow: 'auto', pr: 0.5 }}>
                            {filteredEntries.length === 0 && (
                                <Typography variant="body2" color="text.secondary">
                                    Noch keine Logs eingegangen. Probiere den Probe-Generator oder löse ein echtes Event aus.
                                </Typography>
                            )}
                            {filteredEntries.map((entry, index) => (
                                <Paper
                                    key={`${entry.timestamp}-${index}`}
                                    variant="outlined"
                                    sx={{ p: 1.5, borderRadius: 2, borderColor: 'divider' }}
                                >
                                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" rowGap={0.5}>
                                        <Chip
                                            label={entry.level.toUpperCase()}
                                            size="small"
                                            color={LEVEL_COLOR[entry.level] || 'default'}
                                            variant="filled"
                                        />
                                        <Typography variant="caption" color="text.secondary">
                                            {new Date(entry.timestamp).toLocaleTimeString('de-DE', { hour12: false })}
                                        </Typography>
                                        {entry.component && (
                                            <Chip label={entry.component} size="small" variant="outlined" />
                                        )}
                                        <Box sx={{ flexGrow: 1 }} />
                                        <Typography variant="caption" color="text.secondary">
                                            {entry.stackTrace ? 'Stacktrace verfügbar' : 'Kein Stacktrace'}
                                        </Typography>
                                    </Stack>
                                    <Typography variant="subtitle2" sx={{ mt: 0.5 }}>
                                        {entry.message || '<ohne Nachricht>'}
                                    </Typography>
                                    {entry.context && (
                                        <Tooltip title="Kontext" placement="top-start" arrow>
                                            <pre
                                                style={{
                                                    margin: '8px 0 0',
                                                    padding: '8px',
                                                    background: '#0b0c10',
                                                    color: '#e9eef3',
                                                    borderRadius: 12,
                                                    overflowX: 'auto',
                                                    fontSize: 12,
                                                    maxHeight: 180,
                                                }}
                                            >
                                                {JSON.stringify(entry.context, null, 2)}
                                            </pre>
                                        </Tooltip>
                                    )}
                                    {showStack && entry.stackTrace && (
                                        <Box
                                            sx={{
                                                mt: 1,
                                                p: 1,
                                                bgcolor: (theme) => theme.palette.grey[100],
                                                borderRadius: 1.5,
                                                fontFamily: 'monospace',
                                                fontSize: 12,
                                                whiteSpace: 'pre-wrap',
                                            }}
                                        >
                                            {entry.stackTrace}
                                        </Box>
                                    )}
                                </Paper>
                            ))}
                        </Stack>
                    </Paper>
                </Grid>

                <Grid item xs={12} md={4}>
                    <Stack spacing={2}>
                        <Paper elevation={1} sx={{ p: 2, borderRadius: 3 }}>
                            <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                                Schnelle Kennzahlen
                            </Typography>
                            <Stack spacing={1}>
                                <StatRow label="Gesamt" value={levelStats.total ?? 0} />
                                <StatRow label="Error" value={levelStats.error ?? 0} />
                                <StatRow label="Warn" value={levelStats.warn ?? 0} />
                                <StatRow label="Info" value={levelStats.info ?? 0} />
                                <StatRow label="Debug" value={levelStats.debug ?? 0} />
                                <StatRow label="Trace" value={levelStats.trace ?? 0} />
                            </Stack>
                        </Paper>

                        <Paper elevation={1} sx={{ p: 2, borderRadius: 3 }}>
                            <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                                Umgebung
                            </Typography>
                            <Stack spacing={1}>
                                <MetaRow label="API Base" value={API_BASE_URL} />
                                <MetaRow label="Logging Endpoint" value={REMOTE_LOGGING_URL} />
                                <MetaRow
                                    label="Remote Logging"
                                    value={REMOTE_LOGGING_ENABLED ? 'aktiv' : 'deaktiviert'}
                                />
                                <MetaRow label="Max Einträge" value={maxEntries} />
                                <MetaRow label="Stream" value={streamPaused ? 'pausiert' : 'aktiv'} />
                                <MetaRow label="User Agent" value={typeof navigator !== 'undefined' ? navigator.userAgent : '-'} />
                            </Stack>
                        </Paper>

                        <Paper elevation={1} sx={{ p: 2, borderRadius: 3 }}>
                            <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                                Probe-Generator
                            </Typography>
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                                Erzeugt lokale Log-Einträge und sendet sie (abhängig von den Logging-Settings) auch ans Backend.
                            </Typography>
                            <Stack direction="row" spacing={1} flexWrap="wrap" rowGap={1}>
                                <Button
                                    variant="contained"
                                    startIcon={<PlayCircleFilledWhiteRoundedIcon />}
                                    onClick={() => triggerProbe('info')}
                                >
                                    Info
                                </Button>
                                <Button variant="outlined" onClick={() => triggerProbe('debug')}>
                                    Debug
                                </Button>
                                <Button variant="outlined" color="warning" onClick={() => triggerProbe('warn')}>
                                    Warn
                                </Button>
                                <Button variant="outlined" color="error" onClick={() => triggerProbe('error')}>
                                    Error
                                </Button>
                            </Stack>
                        </Paper>
                    </Stack>
                </Grid>
            </Grid>

            <Divider sx={{ my: 2 }} />
            <Typography variant="caption" color="text.secondary" sx={{ px: 1 }}>
                Live-Daten werden lokal gehalten (konfigurierbare Grenze, aktuell {maxEntries} Einträge). Remote-Logging folgt den Umgebungsvariablen.
            </Typography>
        </Box>
    );
}

function StatRow({ label, value }) {
    return (
        <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="body2" color="text.secondary" sx={{ minWidth: 90 }}>
                {label}
            </Typography>
            <Typography variant="body1" fontWeight={600}>
                {value}
            </Typography>
        </Stack>
    );
}

function MetaRow({ label, value }) {
    return (
        <Stack spacing={0.25}>
            <Typography variant="caption" color="text.secondary">
                {label}
            </Typography>
            <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                {value || '-'}
            </Typography>
        </Stack>
    );
}

